// ModernColorPalette — Modern Layout のカラーパレット (スウォッチ群、§9.6 / consult 2026-06-12).
//
// ## 方針転換 (2026-06-12): Classic 流「旧版16色・ライブ編集スロット」へ作り替え
//
// 当初は静的 50 色プリセット (DEFAULT_MODERN_PALETTE) を描いていたが、これが
// 「ホイール直下に数十色 = CSP のカラーセット (静的ライブラリ)」っぽさの主因だった。
// CSP は色を保存ボタンでパレットに入れる静的ライブラリ型。takamin は **選択スロットが
// ホイール操作に即追従してその場で書き換わる (保存ボタン無し)** = パレットが「作業中の色」
// そのもの、という Classic 一貫モデルに揃えて CSP 指紋を消す。
//
// 本コンポーネントは **表示 + slot 選択発火のみ** を担う:
//   - `palette` (= chrome.paletteSlots、旧版16色 seed の 16 スロット) を 8×2 で描く。
//   - 選択スロット (`selectedIdx`) を inset ring で明確にハイライトする。ライブ編集なので
//     選択スロットの色は chrome.onColorChange の書き戻しでホイールに追従して変わる
//     (= 選択スロットに現在のホイール色がそのまま見える)。
//   - クリックで `onSelectIdx(idx)` を発火 → caller (chrome.onPaletteSlotSelect) が pen を
//     そのスロット色へ倒し、以後のホイール/HEX 編集はそのスロットへ書き戻る。
//   - **ライブ書き戻しロジック (paletteSlots[selectedIdx] = c) は chrome.onColorChange に
//     既にある**ので本ファイルでは書かない (Classic PalettePanel と同じ意味論)。
//
// Classic の `PalettePanel` は **import しない** (Modern 独自スタイルのまま、§9.6)。
// 意味論 (palette / selectedIdx / onSelectIdx) だけ揃え、見た目は Modern の枠なし隣接
// スウォッチ + inset ring を維持する。
//
// ## 最近色行の統合 (段階1、consult 2026-06-12)
//   ライブ編集スロットは選択スロットを上書きするので前の色が消える。その取りこぼしを拾う
//   **履歴ネット**として、heritage 16 スロットの下に細い区切りを挟んで **最近色行 (最大8)**
//   を **同じパネル内** に描く (= 別 section/別パネルにせず cohesive な 1 widget。CSP は
//   カラーサークル/カラーセット/履歴が別パレットで縦積みなのが指紋なので、それを避ける)。
//   最近色クリックは作業スロットへの **読込** (= onRecentSelect(color) → pen.color に載り、
//   ライブ編集モデルで現在の選択スロットへ反映される)。
//   ※ ドラッグ保存 (最近色→スロットへ DnD) は段階2。本ファイルでは実装しない (コメントのみ)。
//
// ## パレットリセット導線 (α feedback 2026-06-13、consult 2026-07-25)
//   heritage 16 スロットは runtime state (`chrome.paletteSlots`) がセッション内のみ生きており
//   初期16色に戻す手段が無かった。最近色行の**さらに下**、区切り線を挟んで小さく置く
//   (= heritage グリッド・最近色グリッドいずれの誤クリック圏内にも入らない配置)。押下は即実行
//   せず、Classic の全消しボタンと同じ既存イディオム `ConfirmDialog` を経由する。
//
// ## 見出し (<header>) の撤去 (2026-08-19)
//
// `<header>パレット</header>` を撤去した (ユーザー判断 2026-08-18、`ModernBrushPanel` の
// ヘッダ撤去 (307f54e) を他パネルへ広げる作業の一環)。16色グリッド + 最近色行を見れば
// パレットであることは自明で、見出しは情報を足していない。パネル名自体は失われない:
// section 要素の `aria-label="パレット"` (支援技術) / `ModernPanelDragShell` のグリップ帯の
// `aria-label`/`title` (マウス hover) / `ModernPanelVisibilityMenu` (パネル名一覧) が引き
// 続き名前を提供する。section は元々 `p-2` を持つため、見出し撤去後も上端の余白は変えて
// いない。

import { useState } from 'react';

import { ConfirmDialog } from '../../ConfirmDialog.js';

const MODERN_SLOT_COLS = 8;

// 最近色行の固定スロット (8 列 1 行)。positional な固定 key を持たせ、render 時に配列
// index を key へ直書きしない (= biome noArrayIndexKey 回避 / スロットは並べ替わらない)。
const RECENT_SLOTS: ReadonlyArray<{ readonly col: number; readonly key: string }> = Array.from(
  { length: MODERN_SLOT_COLS },
  (_, col) => ({ col, key: `recent-slot-${col}` }),
);

export interface ModernColorPaletteProps {
  /** 表示するスロット配列 (= chrome.paletteSlots、旧版16色 seed の 16 個)。 */
  readonly palette: ReadonlyArray<number>;
  /** 選択中スロットの idx。inset ring でハイライトする。 */
  readonly selectedIdx: number;
  /** スロットクリックで選択を移す (= chrome.onPaletteSlotSelect。pen をそのスロット色へ)。 */
  readonly onSelectIdx: (idx: number) => void;
  /** 最近色 (rgb24、most-recent-first、最大8)。空配列なら空行 (プレースホルダ) を出す。 */
  readonly recentColors: ReadonlyArray<number>;
  /** 最近色クリックで作業スロットへ読込 (= pen.color へ。ModernLayout が chrome.onColorChange を渡す)。 */
  readonly onRecentSelect: (color: number) => void;
  /**
   * α feedback (2026-06-13、consult 2026-07-25): heritage 16 スロットを初期色に戻す
   * (= chrome.onPaletteReset)。本 component 側で誤クリック防止の `ConfirmDialog` を
   * 挟んでから呼ぶ (= caller は callback 1 本のみ渡す、Classic PalettePanel と同じ分担)。
   */
  readonly onReset: () => void;
}

const rgb24ToHex = (rgb24: number): string =>
  `#${(rgb24 & 0xffffff).toString(16).padStart(6, '0')}`;

export const ModernColorPalette = ({
  palette,
  selectedIdx,
  onSelectIdx,
  recentColors,
  onRecentSelect,
  onReset,
}: ModernColorPaletteProps): React.ReactNode => {
  // α feedback (2026-06-13): リセット確認モーダルの開閉。Classic PalettePanel と同じ
  // ワンクッション (ConfirmDialog、`window.confirm` は使わない)。
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  return (
    <section
      className="flex flex-col gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] p-2"
      aria-label="パレット"
    >
      {/* 枠・隙間なしで色を隣接させる (consult 2026-06-08 ユーザ指摘)。
          選択/hover は内側 ring (inset) で表現し、隣接スウォッチを侵食しない。
          旧版16色なので 8 列 × 2 行 (= grid-cols-8)。 */}
      <fieldset
        className="m-0 grid grid-cols-8 gap-0 overflow-hidden rounded-sm border-0 p-0"
        aria-label="旧版16色 パレット"
        data-manual="color.palette"
      >
        {palette.map((rgb24, index) => {
          const hex = rgb24ToHex(rgb24);
          // 選択中スロット (idx) を明確にハイライト。ライブ編集なので、この
          // スロット色は chrome.onColorChange の書き戻しでホイールに追従して変わる。
          const selected = index === selectedIdx;
          // key はグリッド座標 (row,col) 由来 = 固定長 16 スロットで安定 (= 並べ替えなし)。
          // ライブ書き戻しで同色が重複しても座標で一意 (生配列 index 直書きは避ける)。
          const row = Math.floor(index / MODERN_SLOT_COLS);
          const col = index % MODERN_SLOT_COLS;
          return (
            <button
              key={`slot-r${row}c${col}`}
              type="button"
              aria-label={`色 ${hex}`}
              aria-pressed={selected}
              title={hex}
              onClick={() => onSelectIdx(index)}
              className={`relative aspect-square w-full ${
                selected
                  ? 'z-10 ring-2 ring-[var(--color-accent)] ring-inset'
                  : 'hover:z-10 hover:ring-2 hover:ring-[var(--color-accent)] hover:ring-inset'
              }`}
              // 自色の 1px outline で隣接スウォッチ間のサブピクセル継ぎ目 (親背景の
              // 透け) を埋める。outline は box-shadow ベースの ring と競合しない
              // (consult 2026-06-08 ユーザ指摘: 微妙に線が入る)。
              style={{ backgroundColor: hex, outline: `1px solid ${hex}` }}
            />
          );
        })}
      </fieldset>

      {/* 細い区切り: heritage スロットと最近色を同一パネル内で視覚的に分ける (= 別パネルに
          積まない統合表示)。装飾用の区切りなので presentation role。 */}
      <hr className="m-0 border-0 border-t border-[var(--color-border)]" />

      {/* 最近色行 (最大8 = 8列1行)。意匠は heritage スロットと揃える (やや小さめ)。
          クリックで作業スロットへ読込 (= onRecentSelect → pen.color)。
          段階2 (将来): 最近色 → 16スロットへの DnD 保存。本行は DnD コードを持たない。 */}
      <fieldset
        className="m-0 grid grid-cols-8 gap-0 overflow-hidden rounded-sm border-0 p-0"
        aria-label="最近色"
        data-manual="color.recent"
      >
        {/* 8 スロット固定枠を slot で描く (= 履歴ゼロ/末尾の空きでもレイアウトを保つ)。
            key は positional な固定リテラル (`recent-slot-N`) を使い、配列 index 直書きを避ける
            (= スロットは並べ替わらない固定位置、最近色は dedupe 済みなので色衝突もない)。 */}
        {RECENT_SLOTS.map((slot) => {
          const rgb24 = recentColors[slot.col];
          // 履歴ゼロ / 末尾の空きは「空スロット」(= 押せない placeholder) でレイアウトを保つ。
          if (rgb24 === undefined) {
            return (
              <div key={slot.key} className="aspect-square w-full bg-[var(--color-bg-soft)]" />
            );
          }
          const hex = rgb24ToHex(rgb24);
          return (
            <button
              key={slot.key}
              type="button"
              aria-label={`最近色 ${hex}`}
              title={`${hex} (クリックで読込)`}
              onClick={() => onRecentSelect(rgb24)}
              className="relative aspect-square w-full hover:z-10 hover:ring-2 hover:ring-[var(--color-accent)] hover:ring-inset"
              style={{ backgroundColor: hex, outline: `1px solid ${hex}` }}
            />
          );
        })}
      </fieldset>

      {/* 細い区切り + リセット導線 (α feedback 2026-06-13)。heritage グリッド・最近色
          グリッドいずれの誤クリック圏内にも入らないよう、2 本目の区切り線の下にさらに
          離して置く。小さく地味な配色にして「常用ボタン」に見せない。 */}
      <hr className="m-0 border-0 border-t border-[var(--color-border)]" />
      <button
        type="button"
        onClick={() => setResetConfirmOpen(true)}
        title="パレットを初期16色に戻す"
        data-manual="color.palette"
        className="rounded px-1 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:bg-[var(--color-bg-soft)] hover:text-white"
      >
        初期色に戻す
      </button>

      <ConfirmDialog
        open={resetConfirmOpen}
        title="パレットをリセット"
        message={'パレットを初期16色に戻しますか?\nカスタムした色は失われます。'}
        confirmLabel="リセット"
        destructive
        onConfirm={() => {
          onReset();
          setResetConfirmOpen(false);
        }}
        onCancel={() => setResetConfirmOpen(false)}
      />
    </section>
  );
};
